/**
 * Analytics Filters Component
 * Multi-level filtering for analytics (Global, Tenant, Organization)
 */

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Globe, Layers, Building2 } from 'lucide-react'
import { useTenants } from '@/features/tenants/hooks/useTenants'
import { useTenantOrganizations } from '@/features/tenants/hooks/useTenantOrganizations'

interface AnalyticsFiltersProps {
  filters: {
    tenantId?: string
    organizationId?: string
  }
  onFiltersChange: (filters: { tenantId?: string; organizationId?: string }) => void
}

export function AnalyticsFilters({ filters, onFiltersChange }: AnalyticsFiltersProps) {
  const { data: tenantsData } = useTenants(1, 100)
  const { data: orgsData } = useTenantOrganizations(filters.tenantId)

  const tenants = tenantsData?.tenants || []
  const organizations = orgsData || []

  // Determine current scope
  const currentScope = filters.organizationId
    ? 'organization'
    : filters.tenantId
    ? 'tenant'
    : 'global'

  const getScopeLabel = () => {
    if (filters.organizationId) {
      const org = organizations.find(o => o._id === filters.organizationId)
      return org ? org.displayName : 'Organization'
    }
    if (filters.tenantId) {
      const tenant = tenants.find(t => t._id === filters.tenantId)
      return tenant ? tenant.displayName : 'Tenant'
    }
    return 'Global'
  }

  const getScopeIcon = () => {
    if (filters.organizationId) return <Building2 className="h-4 w-4" />
    if (filters.tenantId) return <Layers className="h-4 w-4" />
    return <Globe className="h-4 w-4" />
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          {/* Current Scope Indicator */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1.5 px-3 py-1.5">
              {getScopeIcon()}
              <span className="font-medium">
                {getScopeLabel()} Analytics
              </span>
            </Badge>
            <span className="text-sm text-muted-foreground">
              {currentScope === 'global' && 'Viewing all system data'}
              {currentScope === 'tenant' && 'Tenant-specific metrics'}
              {currentScope === 'organization' && 'Organization-specific metrics'}
            </span>
          </div>

          {/* Filter Controls */}
          <div className="flex gap-3 items-center">
            {/* Tenant Selector */}
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <Select
                value={filters.tenantId || 'global'}
                onValueChange={(value) => {
                  onFiltersChange({
                    tenantId: value === 'global' ? undefined : value,
                    organizationId: undefined, // Reset org when tenant changes
                  })
                }}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">
                    <div className="flex items-center gap-2">
                      <Globe className="h-3 w-3" />
                      Global (All Tenants)
                    </div>
                  </SelectItem>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant._id} value={tenant._id}>
                      {tenant.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Organization Selector (only if tenant selected) */}
            {filters.tenantId && (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <Select
                  value={filters.organizationId || 'all'}
                  onValueChange={(value) => {
                    onFiltersChange({
                      ...filters,
                      organizationId: value === 'all' ? undefined : value,
                    })
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org._id} value={org._id}>
                        {org.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
